import React from 'react';
import { CheckIcon, ClockIcon, UserIcon } from 'lucide-react';
import { PanelCard } from './PanelCard';
import { DscrScenario } from '../types/dscr';
import { dscrStatus, formatRatio, formatTimestamp } from '../utils/dscr';
interface ScenarioHistoryPanelProps {
  scenarios: DscrScenario[];
  activeScenarioId: string | null;
  onLoad: (scenario: DscrScenario) => void;
}
const DSCR_TILE_STYLES: Record<string, string> = {
  strong: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  pass: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  marginal: 'border-[#f5a623]/40 bg-[#f5a623]/10 text-[#c47f10]',
  fail: 'border-red-200 bg-red-50 text-red-800'
};
export function ScenarioHistoryPanel({
  scenarios,
  activeScenarioId,
  onLoad
}: ScenarioHistoryPanelProps) {
  return <PanelCard title="Saved Scenarios" icon={<ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-96" data-mp="1m2rxdwr0ursa" />} data-id="element-95" data-mp="1m3t5ptgnl06b">
      {scenarios.length === 0 ? <p className="py-6 text-center text-sm text-slate-500" data-id="element-97" data-mp="1i3su0gkxv32y">
          No scenarios saved yet. Run a what-if and save it to start the history.
        </p> : <ul className="divide-y divide-slate-200" data-id="element-98" data-mp="soqbgy00v5wf">
          {scenarios.map(scenario => {
        const isActive = scenario.id === activeScenarioId;
        const status = dscrStatus(scenario.dscr);
        return <li key={scenario.id} className={`grid grid-cols-1 items-center gap-4 px-2 py-3.5 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.3fr)_8.5rem_6rem] ${isActive ? 'bg-[#0176d3]/5' : ''}`} data-id="element-99" data-mp="iqspyi9jrn7i">
                <div className="min-w-0" data-id="element-100" data-mp="xo36w74yllam">
                  <div className="flex items-center gap-2" data-id="element-101" data-mp="xl7a4m4ul2nr">
                    <p className="truncate text-sm font-semibold text-slate-900" data-id="element-102" data-mp="11ntu60va1jae">
                      {scenario.name}
                    </p>
                    {scenario.version > 1 ? <span className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600" data-id="element-103" data-mp="1u5fw0oj3t5io">
                        v{scenario.version}
                      </span> : null}
                    {isActive ? <span className="inline-flex items-center gap-1 rounded-full bg-[#0176d3] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" data-id="element-104" data-mp="1uzoxy5384cvj">
                        <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-105" data-mp="1l227clckawvq" />
                        Loaded
                      </span> : null}
                  </div>
                  <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500" data-id="element-106" data-mp="1g9r9rp5ann6c">
                    <span className="inline-flex items-center gap-1" data-id="element-107" data-mp="17aw3kd292d31">
                      <UserIcon className="h-3 w-3" aria-hidden="true" data-id="element-108" data-mp="17cqjte2kth0s" />
                      {scenario.version > 1 ? 'Updated by ' : 'Saved by '}
                      {scenario.savedBy}
                    </span>
                    <span className="inline-flex items-center gap-1" data-id="element-109" data-mp="xf2ghzqwn170">
                      <ClockIcon className="h-3 w-3" aria-hidden="true" data-id="element-110" data-mp="n89jgvfhrdr7" />
                      {formatTimestamp(scenario.savedAt)}
                    </span>
                    <span data-id="element-111" data-mp="x6fohlwlh00a">{scenario.units.length} units</span>
                  </p>
                </div>

                <div className="min-w-0" data-id="element-112" data-mp="126bq5ehtkfco">
                  {scenario.note ? <p className="text-xs leading-relaxed text-slate-600" data-id="element-113" data-mp="4ijtclmobg5m">
                      <span className="font-semibold text-slate-700" data-id="element-114" data-mp="4ggr9388xwkj">Notes: </span>
                      {scenario.note}
                    </p> : <p className="text-xs italic text-slate-400" data-id="element-115" data-mp="eewkpcuqxhx7">No notes</p>}
                </div>

                <div className={`rounded border px-3 py-2 text-center ${DSCR_TILE_STYLES[status]}`} data-id="element-116" data-mp="j9ajzqcj4975">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-80" data-id="element-117" data-mp="3mw11hhjbdj0m">DSCR</p>
                  <p className="text-xl font-semibold tabular-nums leading-tight" data-id="element-118" data-mp="1sfsx7ab9acaj">
                    {formatRatio(scenario.dscr)}
                  </p>
                </div>

                <div className="flex md:justify-end" data-id="element-119" data-mp="1nf4z64om0v6t">
                  <button type="button" onClick={() => onLoad(scenario)} className="w-full rounded border border-[#0176d3] bg-white px-3 py-2 text-xs font-semibold text-[#0176d3] transition hover:bg-[#0176d3] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#0176d3]/40 md:w-auto" data-id="element-120" data-mp="226nm6mfvzpzo">
                    Load
                  </button>
                </div>
              </li>;
      })}
        </ul>}
    </PanelCard>;
}